import { Button, Input } from 'antd';

import { useViewNameMaxLength } from 'hooks/ppa/usePpaViews';

import { CheckIcon, CrossIcon, ViewNameClearIcon } from '../SavedViewStateSelect.icons';
import { VIEW_NAME_MAX_LENGTH } from '../savedViewStateSelect.constants';
import { getViewNameValidationError } from '../savedViewStateSelect.helpers';
import {
    RENAME_ACTION_BTN_CLASS,
    VIEW_NAME_INPUT_CLASS,
    VIEW_NAME_INPUT_ERROR_CLASS,
} from '../savedViewStateSelect.styles';
import type { SavedViewRenameEditorProps } from '../SavedViewStateSelect.types';

const SavedViewRenameEditor = ({
    value,
    originalViewName,
    existingViewNames,
    isSaving,
    onChange,
    onCancel,
    onConfirm,
}: SavedViewRenameEditorProps) => {
    const viewNameMaxLength = useViewNameMaxLength() ?? VIEW_NAME_MAX_LENGTH;
    const trimmedValue = value.trim();
    const viewNameError = getViewNameValidationError(value, existingViewNames, originalViewName);
    const isUnchanged = trimmedValue === originalViewName.trim();
    const canConfirm = Boolean(trimmedValue) && !viewNameError && !isUnchanged && !isSaving;

    return (
        <div className="-m-3 bg-[#f4f4f5] px-2 py-4">
            <div className="flex flex-col gap-[6px]">
                <Input
                    size="middle"
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onPressEnter={() => {
                        if (canConfirm) {
                            onConfirm();
                        }
                    }}
                    maxLength={viewNameMaxLength}
                    showCount
                    allowClear={
                        value ? { clearIcon: <ViewNameClearIcon hasError={Boolean(viewNameError)} /> } : false
                    }
                    status={viewNameError ? 'error' : undefined}
                    disabled={isSaving}
                    className={viewNameError ? VIEW_NAME_INPUT_ERROR_CLASS : VIEW_NAME_INPUT_CLASS}
                />
                {viewNameError ? (
                    <span className="text-[12px] leading-[16px] text-[#ff4d4f]">{viewNameError}</span>
                ) : null}
                <div className="flex items-center justify-end gap-[6px]">
                    <Button
                        className={`toolbar-btn ${RENAME_ACTION_BTN_CLASS} !rounded !border-[#d9d9d9]`}
                        onClick={onCancel}
                        disabled={isSaving}
                        aria-label="Cancel rename"
                    >
                        <CrossIcon />
                    </Button>
                    <Button
                        type="primary"
                        className={`${RENAME_ACTION_BTN_CLASS} !rounded !border-[#44338a] !bg-[#44338A] !text-[#ffffff] disabled:!cursor-not-allowed disabled:!border-[#d9d9d9] disabled:!bg-[#f5f5f5] [&:disabled_svg_path]:!fill-[rgba(0,0,0,0.25)]`}
                        onClick={() => {
                            if (canConfirm) {
                                onConfirm();
                            }
                        }}
                        loading={isSaving}
                        disabled={!canConfirm}
                        aria-label="Save view name"
                    >
                        {!isSaving ? <CheckIcon /> : null}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SavedViewRenameEditor;
